import { useState, useEffect, useCallback } from 'react';
import { AlertingService, AlertRule, AlertInstance, NotificationChannel } from '@/services/alerts/alertingService';

export const useAlerting = () => {
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [activeAlerts, setActiveAlerts] = useState<AlertInstance[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<AlertInstance[]>([]);
  const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([]);
  const [alertStats, setAlertStats] = useState<any>({
    total_alerts: 0,
    active_alerts: 0,
    resolved_alerts: 0,
    acknowledged_alerts: 0,
    critical_alerts: 0,
    alert_trend: []
  });
  const [loading, setLoading] = useState(true);

  // Load all alert data
  const loadAlertData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading alert data...');
      
      const [rules, active, recent, channels, stats] = await Promise.all([
        AlertingService.getAlertRules(),
        AlertingService.getActiveAlerts(),
        AlertingService.getRecentAlerts(24),
        AlertingService.getNotificationChannels(),
        AlertingService.getAlertStats(7)
      ]);

      setAlertRules(rules);
      setActiveAlerts(active);
      setRecentAlerts(recent);
      setNotificationChannels(channels);
      setAlertStats(stats);
      console.log('Alert data loaded successfully:', { rules: rules.length, active: active.length, stats });
    } catch (error) {
      console.error('Error loading alert data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadAlertData();
  }, [loadAlertData]);

  // Auto-refresh active alerts every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [active, stats] = await Promise.all([
          AlertingService.getActiveAlerts(),
          AlertingService.getAlertStats(7)
        ]);
        setActiveAlerts(active);
        setAlertStats(stats);
      } catch (error) {
        console.error('Error auto-refreshing alerts:', error);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Create alert rule
  const createAlertRule = useCallback(async (rule: {
    name: string;
    description?: string;
    rule_type: string;
    conditions: any;
    threshold_value: number;
    threshold_operator: string;
    time_window_minutes: number;
    severity: string;
    notification_channels: string[];
  }): Promise<boolean> => {
    const ruleId = await AlertingService.createAlertRule(rule);
    if (ruleId) {
      await loadAlertData(); // Refresh data
      return true;
    }
    return false;
  }, [loadAlertData]);

  // Toggle alert rule status
  const toggleAlertRule = useCallback(async (ruleId: string, isActive: boolean): Promise<boolean> => {
    const success = await AlertingService.updateAlertRuleStatus(ruleId, isActive);
    if (success) {
      setAlertRules(prev => 
        prev.map(rule => 
          rule.id === ruleId 
            ? { ...rule, is_active: isActive }
            : rule
        )
      );
    }
    return success;
  }, []);

  // Acknowledge alert
  const acknowledgeAlert = useCallback(async (alertId: string): Promise<boolean> => {
    const success = await AlertingService.acknowledgeAlert(alertId);
    if (success) {
      setActiveAlerts(prev => 
        prev.map(alert => 
          alert.id === alertId 
            ? { ...alert, status: 'acknowledged' as const, acknowledged_at: new Date().toISOString() }
            : alert
        )
      );
      await loadAlertData(); // Refresh stats
    }
    return success;
  }, [loadAlertData]);

  // Resolve alert
  const resolveAlert = useCallback(async (alertId: string): Promise<boolean> => {
    const success = await AlertingService.resolveAlert(alertId);
    if (success) {
      setActiveAlerts(prev => 
        prev.filter(alert => alert.id !== alertId)
      );
      await loadAlertData(); // Refresh stats
    }
    return success;
  }, [loadAlertData]);

  // Create notification channel
  const createNotificationChannel = useCallback(async (channel: {
    name: string;
    type: string;
    config: any;
  }): Promise<boolean> => {
    const channelId = await AlertingService.createNotificationChannel(channel);
    if (channelId) {
      await loadAlertData(); // Refresh data
      return true;
    }
    return false;
  }, [loadAlertData]);

  // Process alerts manually
  const processAlerts = useCallback(async (): Promise<boolean> => {
    const success = await AlertingService.processAlerts();
    if (success) {
      setTimeout(() => loadAlertData(), 2000); // Refresh after processing
    }
    return success;
  }, [loadAlertData]);

  return {
    // Data
    alertRules,
    activeAlerts,
    recentAlerts,
    notificationChannels,
    alertStats,
    loading,
    
    // Actions
    createAlertRule,
    toggleAlertRule,
    acknowledgeAlert,
    resolveAlert,
    createNotificationChannel,
    processAlerts,
    refreshData: loadAlertData
  };
};

// Hook for browser notifications
export const useBrowserNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported('Notification' in window);
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [supported]);

  const showNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (!supported || permission !== 'granted') return null;

    try {
      return new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }, [supported, permission]);

  const showAlertNotification = useCallback((alert: AlertInstance, rule?: AlertRule) => {
    const title = `Alert: ${rule?.name || 'System Alert'}`;
    const body = alert.message;
    const icon = getSeverityIcon(rule?.severity || 'medium');

    return showNotification(title, {
      body,
      icon,
      tag: `alert-${alert.id}`,
      requireInteraction: rule?.severity === 'critical'
    });
  }, [showNotification]);

  return {
    supported,
    permission,
    requestPermission,
    showNotification,
    showAlertNotification
  };
};

function getSeverityIcon(severity: string): string {
  switch (severity) {
    case 'critical': return '🚨';
    case 'high': return '⚠️';
    case 'medium': return '🔶';
    case 'low': return 'ℹ️';
    default: return '🔔';
  }
}